import { PAYMENT_METHODS } from '@budgie/shared';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { trpc } from '../../lib/trpc';

type Category = {
  id: string;
  name: string;
  paymentMethod: string;
  paymentSource: string | null;
  isRecurring: boolean;
  isVariable: boolean;
  memberId: string | null;
};

type Group = { id: string; name: string };

type Props = {
  groupId: string;
  groups: Group[];
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
};

export function CategoryDialog({ groupId, groups, category, onClose, onSaved }: Props) {
  const [form, setForm] = useState({
    groupId,
    name: category?.name ?? '',
    paymentMethod: category?.paymentMethod ?? 'manuellt',
    paymentSource: category?.paymentSource ?? '',
    isRecurring: category?.isRecurring ?? true,
    isVariable: category?.isVariable ?? false,
    memberId: category?.memberId ?? '',
  });

  const membersQuery = trpc.household.get.useQuery();
  const createMutation = trpc.category.create.useMutation({
    onSuccess: onSaved,
  });
  const updateMutation = trpc.category.update.useMutation({
    onSuccess: onSaved,
  });

  const isEditing = Boolean(category);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      groupId: form.groupId,
      name: form.name.trim(),
      paymentMethod: form.paymentMethod as (typeof PAYMENT_METHODS)[number],
      paymentSource: form.paymentSource || undefined,
      isRecurring: form.isRecurring,
      isVariable: form.isVariable,
      memberId: form.memberId || undefined,
    };

    if (isEditing && category) {
      updateMutation.mutate({ id: category.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const set = (key: string, val: unknown) => setForm((f) => ({ ...f, [key]: val }));
  const members: { id: string; displayName: string }[] =
    (membersQuery.data as { members?: { id: string; displayName: string }[] } | undefined)
      ?.members ?? [];
  const memberSelectValue = form.memberId || '__shared';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Redigera kategori' : 'Lägg till kategori'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="category-group" className="text-xs text-muted-foreground">
              Grupp
            </Label>
            <Select value={form.groupId} onValueChange={(value) => set('groupId', value)}>
              <SelectTrigger id="category-group" className="mt-1 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="category-name" className="text-xs text-muted-foreground">
              Namn
            </Label>
            <Input
              id="category-name"
              required
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="category-payment-method" className="text-xs text-muted-foreground">
                Betalningssätt
              </Label>
              <Select
                value={form.paymentMethod}
                onValueChange={(value) => set('paymentMethod', value)}
              >
                <SelectTrigger id="category-payment-method" className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category-payment-source" className="text-xs text-muted-foreground">
                Konto/källa
              </Label>
              <Input
                id="category-payment-source"
                value={form.paymentSource}
                onChange={(e) => set('paymentSource', e.target.value)}
                placeholder="t.ex. Handelsbanken"
                className="mt-1"
              />
            </div>
          </div>

          {members.length > 0 && (
            <div>
              <Label htmlFor="category-member" className="text-xs text-muted-foreground">
                Tillhör (valfritt)
              </Label>
              <Select
                value={memberSelectValue}
                onValueChange={(value) => set('memberId', value === '__shared' ? '' : value)}
              >
                <SelectTrigger id="category-member" className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__shared">Gemensam</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:gap-6">
            <div className="flex items-center gap-3">
              <Switch
                id="category-recurring"
                checked={form.isRecurring}
                onCheckedChange={(checked) => set('isRecurring', checked === true)}
              />
              <Label htmlFor="category-recurring" className="text-sm text-foreground">
                Återkommande
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="category-variable"
                checked={form.isVariable}
                onCheckedChange={(checked) => set('isVariable', checked === true)}
              />
              <Label htmlFor="category-variable" className="text-sm text-foreground">
                Rörlig
              </Label>
            </div>
          </div>

          {(createMutation.error || updateMutation.error) && (
            <p className="text-destructive text-sm">
              {(createMutation.error ?? updateMutation.error)?.message}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Avbryt
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending
                ? 'Sparar…'
                : isEditing
                  ? 'Spara'
                  : 'Lägg till'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
